import type Phaser from 'phaser';
import type { CameraConfig, InputConfig } from '../config/gameConfig';
import type { ScreenPoint } from '../core/types';
import type { ActionBus } from './actions';
import { KeyboardInput } from './KeyboardInput';
import { PointerInput, type BoxCapture, type DragCapture } from './PointerInput';

/**
 * Owns every input adapter for a scene. Gameplay only sees GameActions on
 * the bus. Mouse, touch (tap, drag, pinch) and pen all go through PointerInput.
 *
 * Guarantees one registration per adapter (attach is idempotent) and full
 * cleanup on destroy, so scene restarts and hot reloads never stack handlers.
 */
export class InputManager {
  private readonly pointer: PointerInput;
  private readonly keyboard: KeyboardInput;

  constructor(scene: Phaser.Scene, bus: ActionBus, inputCfg: InputConfig, camCfg: CameraConfig, dragCapture?: DragCapture, boxCapture?: BoxCapture) {
    this.pointer = new PointerInput(scene.input, bus, inputCfg, camCfg, dragCapture, boxCapture);
    this.keyboard = new KeyboardInput(window, bus, camCfg);
    this.pointer.attach();
    this.keyboard.attach();
  }

  /** Live keyboard listeners across all instances (diagnostics; must be 1). */
  get keyboardListeners(): number {
    return KeyboardInput.attachedInstances;
  }

  /** Where the pointer is over the canvas (null when outside). Used to keep hover correct when the camera moves. */
  get pointerPosition(): ScreenPoint | null {
    return this.pointer.position;
  }

  get isPinching(): boolean {
    return this.pointer.isPinching;
  }

  update(dt: number): void {
    this.keyboard.update(dt);
  }

  destroy(): void {
    this.pointer.detach();
    this.keyboard.detach();
  }
}
